import { useNavigate } from "react-router-dom";
import { ArrowLeft, Bookmark, Check, CircleAlert, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { CoTile, FitRing } from "./parts";

const text = (r) => (typeof r === "string" ? r : r?.text || r?.name || "");

function JobAction({ app, status, onApply, className, testId = "job-primary-action" }) {
  const nav = useNavigate();
  const [label, go] = !app ? ["One-tap apply", onApply]
    : status?.to ? [status.action === "Resume" ? "Resume assessment" : "Start assessment", () => nav(status.to)]
      : ["View application", () => nav(`/candidate/applications?open=${app.id}`)];
  return (
    <button onClick={go} data-testid={testId}
      className={cn("h-11 px-5 rounded-xl text-sm font-semibold transition-[filter,background-color] active:scale-[0.98]",
        app && !status?.to ? "border border-border hover:bg-foreground/[0.04]" : "bg-primary text-primary-foreground hover:brightness-110", className)}>
      {label}
    </button>
  );
}

function FitPanel({ o }) {
  const matched = (o.matched || []).slice(0, 4);
  const gaps = (o.gaps || []).slice(0, 3);
  return (
    <section className="hs-card p-5 sm:p-6 grid sm:grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)] gap-6 items-start" data-testid="job-fit">
      <FitRing value={o.fit} label="Your fit" testId="job-fit-ring" />
      <div>
        <h3 className="text-sm font-semibold text-primary mb-2.5">Why you match</h3>
        {matched.length ? <ul className="space-y-2">{matched.map((m, i) => <li key={i} className="flex gap-2 text-sm"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />{m}</li>)}</ul>
          : <p className="text-sm text-muted-foreground">{o.reason}</p>}
      </div>
      <div>
        <h3 className="text-sm font-semibold text-amber-600 dark:text-amber-300 mb-2.5">Worth strengthening</h3>
        {gaps.length ? <ul className="space-y-2">{gaps.map((g, i) => <li key={i} className="flex gap-2 text-sm"><CircleAlert className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />{g}</li>)}</ul>
          : <p className="text-sm text-muted-foreground">Nothing flagged — you cover the essentials.</p>}
      </div>
    </section>
  );
}

const process = (j) => [
  ["Apply", "Your confirmed profile is sent"],
  ["Assessment", j.assessment_time_limit_min ? `${j.assessment_time_limit_min} min · online` : "Online · untimed"],
  ["Interviews", "With the hiring team"],
  ["Decision", "A person reviews every result"],
];

function JobLists({ job }) {
  const blocks = [["What you'll do", job.responsibilities], ["What we're looking for", job.requirements]].filter(([, l]) => l?.length);
  if (!blocks.length) return null;
  return (
    <div className="grid md:grid-cols-2 gap-6">
      {blocks.map(([title, list]) => (
        <div key={title}>
          <h3 className="text-sm font-semibold mb-2.5">{title}</h3>
          <ul className="space-y-2">{list.map((r, i) => <li key={i} className="flex gap-2.5 text-sm text-foreground/85"><span className="mt-2 h-1 w-1 rounded-full bg-primary shrink-0" />{text(r)}</li>)}</ul>
        </div>
      ))}
    </div>
  );
}

export function JobPane({ o, app, status, ready, saved, onSave, onApply, onBack }) {
  const nav = useNavigate();
  const j = o.job;
  return (
    <>
    <article className="space-y-6 pb-28 lg:pb-0 hs-rise" key={j.id} data-testid="job-pane">
      <button onClick={onBack} className="lg:hidden inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="job-pane-back"><ArrowLeft className="h-4 w-4" /> All roles</button>
      <header className="flex flex-wrap items-start gap-4">
        <CoTile name={o.company_name || j.title} size="lg" />
        <div className="min-w-0 flex-1 basis-60">
          <h1 className="text-2xl sm:text-3xl font-display font-semibold tracking-tight" data-testid="job-pane-title">{j.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{[o.company_name, j.location, j.employment_type, j.seniority].filter(Boolean).join(" · ")}</p>
        </div>
        <div className="flex items-center gap-2.5">
          <button onClick={() => onSave(j.id)} aria-pressed={saved} aria-label={saved ? "Remove from saved roles" : "Save role"} data-testid="job-save-btn"
            className="h-11 w-11 rounded-xl border border-border flex items-center justify-center hover:border-foreground/25 transition-colors">
            <Bookmark className={cn("h-4 w-4", saved && "fill-primary text-primary")} />
          </button>
          <JobAction app={app} status={status} onApply={onApply} className="hidden lg:inline-flex items-center" />
        </div>
      </header>
      {ready && o.fit != null && <FitPanel o={o} />}
      {!ready && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 flex items-center justify-between gap-4" data-testid="build-profile-banner">
          <span className="text-sm flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-500" /> Confirm your profile to see your fit and gaps for this role.</span>
          <button onClick={() => nav("/candidate/profile")} className="text-sm font-semibold text-primary hover:underline shrink-0" data-testid="go-build-profile">Build profile</button>
        </div>
      )}
      <ol className="grid grid-cols-2 xl:grid-cols-4 gap-3" data-testid="job-process">
        {process(j).map(([t, s], i) => (
          <li key={t} className="rounded-xl border border-border p-4">
            <div className="hs-mono text-xs text-muted-foreground">{i + 1}</div>
            <div className="text-sm font-semibold mt-1">{t}</div>
            <div className="text-[13px] text-muted-foreground">{s}</div>
          </li>
        ))}
      </ol>
      {j.summary && <p className="text-[15px] leading-relaxed text-foreground/85 max-w-3xl" data-testid="job-pane-summary">{j.summary}</p>}
      <JobLists job={j} />
    </article>
    <div className="lg:hidden fixed inset-x-0 bottom-16 md:bottom-0 z-30 p-4 border-t border-border bg-background/95 backdrop-blur-xl" data-testid="job-mobile-action-bar">
      <JobAction app={app} status={status} onApply={onApply} className="w-full" testId="job-mobile-primary-action" />
    </div>
    </>
  );
}
